import { apiClient } from "../api/ApiClient";

const OrderApiService = {
  /**
   * Initiate checkout process (reserve items and create checkout session)
   * @param {Array} items - Array of cart items to checkout
   * @param {boolean} useCartItems - Whether to use logged-in user's cart items
   * @returns {Promise} Checkout session details
   */
  initiateCheckout: async (items, useCartItems = false) => {
    try {
        const response = await apiClient.post("api/orders/checkout/initiate", {
            items,
            useCartItems
        });
        
        // If there are updated cart items, update local state
        if (response.data.updatedCartItems) {
            return {
                ...response.data,
                shouldUpdateCart: true,
                updatedCartItems: response.data.updatedCartItems
            };
        }
        
        return response.data;
    } catch (error) {
        console.error("Error initiating checkout:", error);
        throw error;
    }
},

  /**
   * Complete checkout with payment details
   * @param {string} checkoutId - Checkout session ID
   * @param {Object} checkoutData - Payment and shipping details
   * @returns {Promise} Order confirmation
   */
  completeCheckout: async (checkoutId, checkoutData) => {
    try {
      const formData = new FormData();

      formData.append("checkoutData", new Blob([JSON.stringify(checkoutData)], {
                type: 'application/json'
            }));
      
      
      if (checkoutData.paymentProofFile) {
        formData.append('paymentProofFile', checkoutData.paymentProofFile);
      }
      
      const response = await apiClient.post(
        `api/orders/checkout/complete/${checkoutId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          transformRequest: [(data) => data],
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error completing checkout:", error);
      throw error;
    }
  },

  /**
   * Cancel checkout session and release reserved items
   * @param {string} checkoutId - Checkout session ID
   * @returns {Promise} Cancellation confirmation
   */
  cancelCheckout: async (checkoutId) => {
    try {
      const response = await apiClient.post(`api/orders/checkout/cancel/${checkoutId}`);
      return response.data;
    } catch (error) {
      console.error("Error canceling checkout:", error);
      throw error;
    }
  },

  /**
   * Get checkout session details
   * @param {string} checkoutId - Checkout session ID
   * @returns {Promise} Checkout session details
   */
  getCheckoutSession: async (checkoutId) => {
    try {
      const response = await apiClient.get(`api/orders/checkout/session/${checkoutId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching checkout session:", error);
      throw error;
    }
  },

  /**
   * Get order by ID
   * @param {string} orderId - Order ID
   * @returns {Promise} Order details
   */
  getOrder: async (orderId) => {
    try {
      const response = await apiClient.get(`api/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching order:", error);
      throw error;
    }
  },

  /**
   * Get user's orders
   * @param {Object} params - Query parameters (page, limit, status, etc.)
   * @returns {Promise} List of orders
   */
  getOrders: async (params = {}) => {
    try {
      const response = await apiClient.get("api/orders", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching orders:", error);
      throw error;
    }
  },

  /**
   * Check cart items availability before checkout
   * @param {Array} items - Array of cart items to check
   * @returns {Promise} Availability check results
   */
  checkCartAvailability: async (items) => {
    try {
      const response = await apiClient.post("api/orders/checkout/check-availability", {
        items
      });
      return response.data;
    } catch (error) {
      console.error("Error checking cart availability:", error);
      throw error;
    }
  },

  /**
   * Upload payment proof image
   * @param {File} file - Image file
   * @returns {Promise} Uploaded file URL
   */
  uploadPaymentProof: async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await apiClient.post(
        "api/orders/upload-payment-proof",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading payment proof:", error);
      throw error;
    }
  },

  /**
   * Track order status
   * @param {string} orderNumber - Order tracking number
   * @returns {Promise} Order status details
   */
  trackOrder: async (orderNumber) => {
    try {
      const response = await apiClient.get(`api/orders/track/${orderNumber}`);
      return response.data;
    } catch (error) {
      console.error("Error tracking order:", error);
      throw error;
    }
  },
  getUserOrders: async (page = 0, size = 5) => {
    try {
      const response = await apiClient.get('api/orders/user/my-orders', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching user orders:', error);
      throw error;
    }
  },
  validatePromoCode: async (promoCode, totalAmount, userId = null) => {
    try {
      const response = await apiClient.post('/api/promocodes/validate', {
        promoCode,
        totalAmount,
        userId
      });
      return response.data;
    } catch (error) {
      console.error('Error validating promo code:', error);
      throw error;
    }
  }
};

export default OrderApiService;
